import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import apiClient from '../../services/apiClient';
import { useAuth } from '../../contexts/AuthContext';

interface StoredAcceptanceSettings {
  enabledForms: Record<string, boolean>;
}

interface AcceptanceModeContextValue extends StoredAcceptanceSettings {
  providerMounted: boolean;
  globalEnabled: boolean;
  canManageGlobal: boolean;
  settingsLoading: boolean;
  isEnabled: (formId: string) => boolean;
  setGlobalEnabled: (enabled: boolean) => Promise<void>;
  setFormEnabled: (formId: string, enabled: boolean) => void;
}

const storageKey = 'erp.acceptance-mode.settings.v1';
const initialSettings: StoredAcceptanceSettings = { enabledForms: {} };

const readSettings = (): StoredAcceptanceSettings => {
  try {
    const raw = localStorage.getItem(storageKey);
    return raw ? { ...initialSettings, ...JSON.parse(raw) } : initialSettings;
  } catch {
    return initialSettings;
  }
};

const AcceptanceModeContext = createContext<AcceptanceModeContextValue | null>(null);

const disabledAcceptanceMode: AcceptanceModeContextValue = {
  enabledForms: {},
  providerMounted: false,
  globalEnabled: false,
  canManageGlobal: false,
  settingsLoading: false,
  isEnabled: () => false,
  setGlobalEnabled: async () => {
    throw new Error('AcceptanceModeProvider is not mounted');
  },
  setFormEnabled: () => undefined,
};

export const AcceptanceModeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<StoredAcceptanceSettings>(readSettings);
  const [globalEnabled, setServerGlobalEnabled] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const { user, permissions } = useAuth();
  const userRoles = user && Array.isArray(user.roles) ? user.roles : [];
  const canManageGlobal = !!user && (
    userRoles.some(role => typeof role === 'string' && role.toUpperCase() === 'ADMIN') ||
    permissions.includes('ADMIN')
  );

  const persist = useCallback((next: StoredAcceptanceSettings) => {
    setSettings(next);
    localStorage.setItem(storageKey, JSON.stringify(next));
  }, []);

  const refreshServerSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const response = await apiClient.get('/api/acceptance/settings');
      setServerGlobalEnabled(!!response.data.globalEnabled);
    } catch {
      setServerGlobalEnabled(false);
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) void refreshServerSettings();
    else setSettingsLoading(false);
  }, [refreshServerSettings, user]);

  const value = useMemo<AcceptanceModeContextValue>(() => ({
    ...settings,
    providerMounted: true,
    globalEnabled,
    canManageGlobal,
    settingsLoading,
    isEnabled: (formId: string) => globalEnabled || !!settings.enabledForms[formId],
    setGlobalEnabled: async (enabled: boolean) => {
      if (!canManageGlobal) throw new Error('ADMIN permission is required');
      const response = await apiClient.put('/api/acceptance/settings', { globalEnabled: enabled });
      setServerGlobalEnabled(!!response.data.globalEnabled);
    },
    setFormEnabled: (formId: string, enabled: boolean) => persist({
      ...settings,
      enabledForms: { ...settings.enabledForms, [formId]: enabled },
    }),
  }), [canManageGlobal, globalEnabled, persist, settings, settingsLoading]);

  return <AcceptanceModeContext.Provider value={value}>{children}</AcceptanceModeContext.Provider>;
};

export const useAcceptanceMode = () => {
  const value = useContext(AcceptanceModeContext);
  return value ?? disabledAcceptanceMode;
};
